import { makeVar } from '@apollo/client'
import { cloneDeep } from '@apollo/client/utilities/common/cloneDeep'

/**
 * `createReactiveVarStore` is a utility function that creates a reactive variable with an initial state and a mutation function to update the state.
 * **Note:** this can only accept an object type as the initial state.
 *
 * @template T - The type of the object that represents the initial state.
 * @param {T} initialStore - The initial state of the reactive variable.
 * @returns {Array} A tuple where the first element is the reactive variable and the second element is the mutation function.
 *
 * @example
 * const [reactiveCounter, setReactiveCounter] = useReactiveVarExtension({ count: 0 })
 * setReactiveVar({ count: reactiveCounter().count + 1 }) // also, no need to explicitly spread the current state(s) as it will handle the merge for you
 */
export const createReactiveVarStore = <T extends object = {}>(initialStore: T) => {
    const reactiveObj = makeVar<T>(initialStore)

    // mutation function to update the reactiveVar
    const mutate = (
        updates: Partial<T> | undefined | null | 'default',
        isResetWithUpdate: boolean = false
    ) => {
        // if user sends null or undefined or 'default', then return the initial state
        if (updates == null || updates === 'default') {
            return reactiveObj(initialStore)
        }

        return reactiveObj({
            // if user wants to reset the reactiveVar with the initial state
            ...cloneDeep(isResetWithUpdate ? initialStore : reactiveObj()),
            // to override the initial state
            ...updates,
        })
    }

    return [reactiveObj, mutate] as const
}
